import { useState, useEffect, useCallback, useMemo } from 'react';
import { type AsociacionRequest, type AsociacionFormErrors } from '../types/asociacion';
import { AsociacionValidation } from '../services/asociacionValidation';

export interface UseAsociacionFormProps {
  initialAsociacionRequest: AsociacionRequest | null;
  isOpen: boolean;
  onSubmit: (asociacion: AsociacionRequest) => void;
}

export interface UseAsociacionFormReturn {
  formData: AsociacionRequest | null;
  errors: AsociacionFormErrors;
  isValid: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
  handleFieldChange: (field: keyof AsociacionRequest, value: unknown) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
  validateForm: () => boolean;
}

export const useAsociacionForm = ({
  initialAsociacionRequest,
  isOpen,
  onSubmit,
}: UseAsociacionFormProps): UseAsociacionFormReturn => {
  const [formData, setFormData] = useState<AsociacionRequest | null>(null);
  const [errors, setErrors] = useState<AsociacionFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setFormData(null);
    setErrors({});
    setIsDirty(false);
    setIsSubmitting(false);
  }, []);

  // Reset form when dialog opens/closes or asociacion changes
  useEffect(() => {
    if (isOpen) {
      if (initialAsociacionRequest) {
        setFormData({ ...initialAsociacionRequest });
      } else {
        // Create empty form for new asociacion
        setFormData({
          descripcion: '',
          idProcedencia: undefined,
        });
      }
      setErrors({});
      setIsDirty(false);
      setIsSubmitting(false);
    } else {
      resetForm();
    }
  }, [initialAsociacionRequest, isOpen, resetForm]);

  const validateForm = useCallback((): boolean => {
    if (!formData) return false;

    const validationErrors = AsociacionValidation.validateAsociacion(formData);
    setErrors(validationErrors);

    return AsociacionValidation.isValidAsociacion(formData);
  }, [formData]);

  const handleFieldChange = useCallback((field: keyof AsociacionRequest, value: unknown) => {
    setFormData(current => {
      if (!current) return null;

      const updated = {
        ...current,
        [field]: value,
      };

      // Mark form as dirty
      if (!isDirty) {
        setIsDirty(true);
      }

      // Re-validate the field immediately
      const fieldErrors = AsociacionValidation.validateAsociacion(updated);
      setErrors(currentErrors => ({
        ...currentErrors,
        [field]: fieldErrors[field as keyof AsociacionFormErrors],
      }));

      return updated;
    });
  }, [isDirty]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!formData || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const isFormValid = validateForm();
      if (isFormValid) {
        await onSubmit(formData);
        resetForm();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isSubmitting, validateForm, onSubmit, resetForm]);

  const isValid = useMemo(() => {
    return formData ? AsociacionValidation.isValidAsociacion(formData) : false;
  }, [formData]);

  return {
    formData,
    errors,
    isValid,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleSubmit,
    resetForm,
    validateForm,
  };
};